import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { type DefaultValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import { useOrderActions } from '../../../../contexts/OrderActionsContext';
import { OrderSchemaType, orderSchema } from './order.schema';
import { ItemOrderSchemaType } from './order.schema';
import { createSteelOrder, fetchServices, fetchScripts, fetchOrderById, fetchSellers, fetchOwnCompanies, updateSteelOrder } from './api';
import UserContext from '../../../../contexts/UserContext';
import { itemInitialState, DUSTRE_ES_CNPJ } from './constants';

import { Card, Separator, Container } from './style';
import MainInfos from '../../OrderForm/Sections/MainInfos';
import Details from '../../OrderForm/Sections/Details';
import ContactField from './components/ContactField';
import AttachmentsField from './components/AttachmentsField';
import { ShippingValueField, ShippingDeadlineField, CarrierField } from './Sections/ShippingSection';
import { ProductionObservationField } from './Sections/ProductionObservationField';
import PaymentSection from './Sections/PaymentSection';
import TaxationSection from './Sections/TaxationSection';
import SteelPaymentMethodDescription from './components/SteelPaymentMethodDescription';
import SteelOrderStepper from './components/SteelOrderStepper';
import { ORDER_SECTION_IDS } from '../../OrderForm/constants';
import { useSteelInstallments } from './Hooks/useSteelInstallments';
import { ApiDataProvider } from './contexts/ApiDataContext';
import { SteelItemsProvider, SteelItemsState } from './contexts/SteelItemsContext';
import OrderForm from '../../OrderForm';
import SteelOrderSaveBridge, { SubmitHelpers } from './components/SteelOrderSaveBridge';
import SteelAutofillBridge from './components/SteelAutofillBridge';
import SteelOrderModals from './components/SteelOrderModals';
import { steelItemsModule } from './steelItemsModule';
import ConfirmationModal from '../../../../components/ModalConfirmation';

// OrderItemTax volta do backend com Decimal em string; o form trabalha com number.
const mapProjecaoTributaria = (tax: any) => {
  if (!tax) return undefined;
  const num = (v: unknown) =>
    v === null || v === undefined || v === '' ? undefined : Number(v);
  return {
    taxScenarioId: tax.taxScenarioId ?? undefined,
    aliquotaICMS: num(tax.aliquotaICMS),
    aliquotaIPI: num(tax.aliquotaIPI),
    aliquotaIBS: num(tax.aliquotaIBS),
    aliquotaCBS: num(tax.aliquotaCBS),
    aliquotaPIS: num(tax.aliquotaPIS),
    aliquotaCOFINS: num(tax.aliquotaCOFINS),
    aliquotaICMSResolvida: num(tax.aliquotaICMSResolvida),
    aliquotaIPIResolvida: num(tax.aliquotaIPIResolvida),
    aliquotaIBSResolvida: num(tax.aliquotaIBSResolvida),
    aliquotaCBSResolvida: num(tax.aliquotaCBSResolvida),
    resolvidoEm: tax.resolvidoEm ?? undefined,
  };
};

const AddOrder: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const { orderId } = useParams<{ orderId: string }>();
  const { setFeedback } = useContext(FeedbackContext);
  const { user } = useContext(UserContext);

  const isEditMode = Boolean(orderId);

  const { data: existingOrder, isLoading: isLoadingOrder } = useQuery({
    queryKey: ['steelOrder', orderId],
    queryFn: () => fetchOrderById(orderId!),
    enabled: isEditMode,
    staleTime: 0,
  });

  const { setSavingOrder } = useOrderActions();

  const { mutate: submitOrder, isPending: isSubmitting } = useMutation({
    mutationFn: isEditMode
      ? (data: OrderSchemaType) => updateSteelOrder(orderId!, data)
      : createSteelOrder,
    onSuccess: (data) => {
      const action = isEditMode ? 'atualizado' : 'criado';
      setFeedback({ message: `Pedido ${data.id || ''} ${action} com sucesso!`, isOpen: true, statusOk: true });
      if (returnTo) {
        navigate(returnTo, { replace: true });
      } else {
        navigate('/panel/steel');
      }
    },
    onError: (error) => {
      const action = isEditMode ? 'atualizar' : 'criar';
      setFeedback({ message: `Erro ao ${action} pedido: ${error.message}`, isOpen: true, statusOk: false });
    },
  });

  useEffect(() => {
    setSavingOrder(isSubmitting);
    return () => setSavingOrder(false);
  }, [isSubmitting, setSavingOrder]);

  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [isNoItemsModalOpen, setIsNoItemsModalOpen] = useState(false);
  const [noSavedItemsType, setNoSavedItemsType] = useState<'no-items' | 'not-saved'>('no-items');
















  const servicesQuery = useQuery({
    queryKey: ['services'],
    queryFn: fetchServices,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const scriptsQuery = useQuery({
    queryKey: ['scripts'],
    queryFn: fetchScripts,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const onValidSubmit = useCallback((data: OrderSchemaType, { setHasValidationError, itemsPayload }: SubmitHelpers) => {
    const savedItems = itemsPayload;

    // Bloqueia salvar se a soma das parcelas != total ou houver parcela zerada.
    const parcelas = Array.isArray(data.installments) ? data.installments : [];
    if (parcelas.length > 0) {
      const orderTotal = Number(data.total) || 0;
      const somaParcelas = parcelas.reduce((acc: number, p: any) => acc + (Number(p?.valor) || 0), 0);
      const hasParcelaZerada = parcelas.some((p: any) => !(Number(p?.valor) > 0));
      const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

      if (hasParcelaZerada || Math.abs(somaParcelas - orderTotal) > 0.01) {
        setHasValidationError(true);
        setFeedback({
          message: hasParcelaZerada
            ? 'Há parcela(s) com valor zerado. Ajuste os valores das parcelas antes de salvar.'
            : `A soma das parcelas (${fmt(somaParcelas)}) precisa ser igual ao total do pedido (${fmt(orderTotal)}).`,
          isOpen: true,
          statusOk: false,
        });
        document.getElementById(ORDER_SECTION_IDS.payment)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }

    const {
      paymentConditionName,
      paymentMethodId,
      paymentMethodName,
      paymentMethodDescription,
      ...orderData
    } = data as any;

    const paymentMethodsToSend = paymentMethodId
      ? [
        {
          paymentMethodId,
          ...(paymentMethodName === 'Outros' && paymentMethodDescription
            ? { description: paymentMethodDescription }
            : {}),
        },
      ]
      : undefined;

    const installmentsToSend = Array.isArray(data.installments)
      ? data.installments.map((parcela: any, idx: number) => {
        const comissaoAtiva = !!parcela.comissaoAtiva;
        return {
          numero: idx + 1,
          valor: Number(parcela.valor) || 0,
          vencimento: parcela.vencimento,
          comissaoAtiva,
          ...(comissaoAtiva ? { comissaoPercentual: Number(parcela.comissaoPercentual) || 0 } : {}),
        };
      })
      : undefined;

    const dataToSubmit = {
      ...orderData,
      items: savedItems,
      ...(installmentsToSend && installmentsToSend.length > 0
        ? { installments: installmentsToSend }
        : { installments: undefined }),
      paymentMethods: paymentMethodsToSend,
    };
    setHasValidationError(false);
    submitOrder(dataToSubmit);
  }, [submitOrder, setFeedback]);

  const onInvalidSubmit = useCallback(() => {
    setIsValidationModalOpen(true);
  }, []);

  const handleGuardFailure = useCallback(() => {
    setIsNoItemsModalOpen(true);
  }, []);







  const loadedValues = useMemo<OrderSchemaType | undefined>(() => {
    if (isEditMode && existingOrder) {
      const safeOrder: OrderSchemaType = {
        ...existingOrder,
        supplier: existingOrder.supplier === null ? '' : existingOrder.supplier,
        supplierId: existingOrder.supplierId === null ? undefined : existingOrder.supplierId,
        seller: existingOrder.seller === null ? '' : existingOrder.seller,
        sellerId: existingOrder.sellerId === null ? undefined : existingOrder.sellerId,
        client: existingOrder.client === null ? '' : existingOrder.client,
        clientId: existingOrder.clientId === null ? undefined : existingOrder.clientId,
        supplierDetails: existingOrder.supplierDetails ? {
          ...existingOrder.supplierDetails,
          companyName: existingOrder.supplierDetails.companyName || existingOrder.supplierDetails.name || ''
        } as Record<string, any> : undefined,
        clientDetails: existingOrder.clientDetails ? {
          ...existingOrder.clientDetails,
          companyName: existingOrder.clientDetails.companyName || existingOrder.clientDetails.name || ''
        } as Record<string, any> : undefined,
        sellerDetails: existingOrder.sellerDetails ? {
          ...existingOrder.sellerDetails,
          name: existingOrder.sellerDetails.name || ''
        } as Record<string, any> : undefined,
        orderTypeId: existingOrder.orderTypeId ?? undefined,
        orderType: existingOrder.orderType ?? '',
        billingDate: existingOrder.billingDate ?? null,
        clientOrderNumber: existingOrder.clientOrderNumber ?? undefined,
        representativeId: existingOrder.representativeId ?? undefined,
        representativeName: existingOrder.representativeName ?? '',
        orderSituationId: existingOrder.orderSituationId ?? undefined,
        orderSituationName: existingOrder.orderSituationName ?? '',
        generalClassId: existingOrder.generalClassId ?? undefined,
        generalClassName: existingOrder.generalClassName ?? '',
        sellingPrice: existingOrder.sellingPrice ?? undefined,
        paymentConditionId: existingOrder.paymentConditionId ?? undefined,
        paymentConditionName: existingOrder.paymentConditionName ?? undefined,
        // Forma flatten no api.ts (mirror de paymentConditionName); backend inclui OrderPaymentMethod no findOne.
        paymentMethodId: existingOrder.paymentMethodId ?? undefined,
        paymentMethodName: existingOrder.paymentMethodName ?? undefined,
        paymentMethodDescription: existingOrder.paymentMethodDescription ?? undefined,
        shippingTypeId: existingOrder.shippingTypeId ?? undefined,
        shippingTypeName: existingOrder.shippingTypeName ?? undefined,
        shippingValue: existingOrder.shippingValue ?? 0,
        items: existingOrder.items.map(item => ({
          ...item,
          isSaved: true,
          tax: mapProjecaoTributaria((item as any).OrderItemTax),
          priceDetails: {
            ...item.priceDetails,
            sellingPrice: item.sellingPrice ?? undefined,
          },
          subItems: item.subItems?.map((subItem: any) => ({
            ...subItem,
            isSaved: true,
            priceDetails: {
              ...subItem.priceDetails,
              sellingPrice: subItem.sellingPrice ?? undefined,
            },
          })) || []
        }))
      };
      return safeOrder;
    }
    return undefined;
  }, [existingOrder, isEditMode]);

  const isLoadingData = servicesQuery.isLoading || scriptsQuery.isLoading || (isEditMode && isLoadingOrder);

  if (isLoadingData) {
    const loadingMessage = isEditMode ? 'Carregando dados do pedido...' : 'Carregando dados do formulário...';
    return <div>{loadingMessage}</div>;
  }

  return (
    <Container>
      <OrderForm<OrderSchemaType>
        resolver={zodResolver(orderSchema)}
        defaultValues={{
          items: [itemInitialState],
          attachments: [],
          shippingValue: 0,
          carrier: { id: 0, companyName: '' },
          criticalAnalysis: false,
        } as DefaultValues<OrderSchemaType>}
        loadedValues={loadedValues}
        itemsModule={steelItemsModule}
        supplier={{ apiUrl: `${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`, queryParams: { isMain: 'true' } }}
        seller={{ apiUrl: `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders/select-in-order/sellers` }}
        client={{ apiUrl: `${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`, queryParams: { companyType: 'Cliente' } }}
        contactSlot={<ContactField />}
        attachmentsSlot={<AttachmentsField />}
        productionObservationSlot={<ProductionObservationField />}
        shippingValueSlot={<ShippingValueField />}
        carrierSlot={<CarrierField />}
        deadlineSlot={<ShippingDeadlineField />}
        stepper={<SteelOrderStepper />}
        taxationSlot={<TaxationSection />}
        installmentsSlot={<PaymentSection isEditMode={isEditMode} loadedConditionId={loadedValues?.paymentConditionId} />}
        methodDescriptionSlot={<SteelPaymentMethodDescription />}
      >
        <SteelAutofillBridge isEditMode={isEditMode} />
        <SteelOrderSaveBridge
          onValidSubmit={onValidSubmit}
          onInvalidSubmit={onInvalidSubmit}
          onGuardFailure={handleGuardFailure}
        />
        <SteelOrderModals
          isNoItemsModalOpen={isNoItemsModalOpen}
          onCloseNoItems={() => setIsNoItemsModalOpen(false)}
          isValidationModalOpen={isValidationModalOpen}
          onCloseValidation={() => setIsValidationModalOpen(false)}
        />
      </OrderForm>
    </Container>
  );
};

export default AddOrder;